import { useQuery, useQueryClient } from '@tanstack/react-query';
import { initialInterfaceAppearance, type InterfaceAppearanceDto } from '@/app/models/InterfaceAppearanceDto';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { getDetails } from './core/_requests';

export const useInterfaceAppearance = () => {
  const { auth, currentUser } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['interfaceAppearance', currentUser?.environment_id, currentUser?.id];
  const enabled = Boolean(auth?.token && currentUser?.id);
  const { data, isPending } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getDetails();
      return response.data;
    },
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: { suppressGlobalError: true },
  });

  const savedAppearance = enabled ? data : undefined;
  const systemName = savedAppearance?.system_name?.trim() || initialInterfaceAppearance.system_name;
  const primaryColor = savedAppearance?.primary_color || initialInterfaceAppearance.primary_color;
  const sidebarColor = savedAppearance?.sidebar_color || initialInterfaceAppearance.sidebar_color;
  const backgroundColor = savedAppearance?.background_color || initialInterfaceAppearance.background_color;
  const logo = savedAppearance?.logo ?? null;
  const splashType = savedAppearance?.splash_type ?? initialInterfaceAppearance.splash_type;
  const splashMedia = savedAppearance?.splash_media ?? null;
  const splashBackgroundColor =
    savedAppearance?.splash_background_color ?? initialInterfaceAppearance.splash_background_color;
  const splashDarkBackgroundColor =
    savedAppearance?.splash_dark_background_color ?? initialInterfaceAppearance.splash_dark_background_color;
  const appearance: InterfaceAppearanceDto = {
    system_name: systemName,
    primary_color: primaryColor,
    sidebar_color: sidebarColor,
    background_color: backgroundColor,
    logo,
    splash_type: splashType,
    splash_media: splashMedia,
    splash_background_color: splashBackgroundColor,
    splash_dark_background_color: splashDarkBackgroundColor,
  };

  const updateAppearance = (nextAppearance: InterfaceAppearanceDto) => {
    queryClient.setQueryData<InterfaceAppearanceDto>(queryKey, nextAppearance);
  };

  const hasSavedAppearance = enabled && data !== undefined;
  const loadingAppearance = enabled && isPending;
  return { appearance, updateAppearance, hasSavedAppearance, loadingAppearance };
};
